import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Paper from '@mui/material/Paper'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { usePaymentStore } from '../stores/paymentStore'
import { useSessionStore } from '../state'
import { requestPaymentIntent, PaymentError } from '../services/paymentService'
import PaymentCountdown from '../components/PaymentCountdown'
import routes from './routes'

const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const stripePromise = stripeKey ? loadStripe(stripeKey) : null

interface CheckoutFormProps {
  joinRequestId: string
  onExpired: () => void
}

const CheckoutForm = ({ joinRequestId, onExpired }: CheckoutFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const { status, deadline, error, setStatus, setError } = usePaymentStore()
  const [stripeReady, setStripeReady] = useState(false)
  const [stripeLoadError, setStripeLoadError] = useState(false)

  const isExpired = status === 'expired'
  const isConfirming = status === 'confirming'
  const isPaid = status === 'paid'

  const handleExpired = useCallback(() => {
    setStatus('expired')
    onExpired()
  }, [setStatus, onExpired])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements || isExpired) return

    setStatus('confirming')
    setError(null)

    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${routes.dives}`
        },
        redirect: 'if_required'
      })

      if (stripeError) {
        setStatus('awaiting')
        setError(stripeError.message || 'Payment failed. Please try again.')
      } else {
        setStatus('paid')
      }
    } catch {
      setStatus('awaiting')
      setError('A network error occurred. Please try again.')
    }
  }

  if (isPaid) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant='h5' color='success.main' gutterBottom>
          Payment successful
        </Typography>
        <Typography variant='body1' sx={{ mb: 3 }}>
          Your spot on the dive is confirmed.
        </Typography>
        <Button variant='contained' onClick={() => navigate(routes.dives)}>
          View Dives
        </Button>
      </Box>
    )
  }

  if (stripeLoadError) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        Unable to load the payment form. Please refresh the page or try a
        different browser. If the issue persists, contact support.
      </Alert>
    )
  }

  return (
    <Box component='form' onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {deadline && (
        <Box sx={{ mb: 2 }}>
          <PaymentCountdown deadline={deadline} onExpired={handleExpired} />
        </Box>
      )}

      {isExpired && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Payment window expired. You can no longer complete this payment.
        </Alert>
      )}

      {error && !isExpired && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          opacity: isExpired ? 0.5 : 1,
          pointerEvents: isExpired ? 'none' : 'auto'
        }}
      >
        <PaymentElement
          onReady={() => setStripeReady(true)}
          onLoadError={() => setStripeLoadError(true)}
        />
      </Box>

      <Button
        type='submit'
        variant='contained'
        fullWidth
        size='large'
        disabled={
          !stripe || !elements || !stripeReady || isExpired || isConfirming
        }
      >
        {isConfirming ? (
          <CircularProgress size={24} color='inherit' />
        ) : (
          'Pay now'
        )}
      </Button>
    </Box>
  )
}

const Payment = () => {
  const { joinRequestId } = useParams<{ joinRequestId: string }>()
  const profile = useSessionStore(s => s.profile)
  const {
    clientSecret,
    status,
    error,
    setClientSecret,
    setDeadline,
    setStatus,
    setError,
    clear
  } = usePaymentStore()
  const [networkError, setNetworkError] = useState(false)
  const [useCredit, setUseCredit] = useState(false)
  const [creditProcessing, setCreditProcessing] = useState(false)
  const [creditSuccess, setCreditSuccess] = useState(false)
  const navigate = useNavigate()

  const creditBalance = profile?.creditBalance ?? 0

  const fetchPaymentIntent = useCallback(async () => {
    if (!joinRequestId) return

    setStatus('loading')
    setError(null)
    setNetworkError(false)

    try {
      const response = await requestPaymentIntent(joinRequestId)
      setClientSecret(response.clientSecret)
      setDeadline(response.deadline)
      setStatus('awaiting')
    } catch (err) {
      if (err instanceof PaymentError) {
        if (err.code === 'PAYMENT_EXPIRED') {
          setStatus('expired')
          setError('Payment window has expired.')
        } else if (err.code === 'DIVE_CANCELLED') {
          setStatus('error')
          setError(
            'This dive has been cancelled. Payment is no longer accepted.'
          )
        } else if (err.code === 'NETWORK_ERROR') {
          setNetworkError(true)
          setStatus('error')
          setError('Unable to connect. Please check your internet connection.')
        } else {
          setStatus('error')
          setError(err.message)
        }
      } else {
        setNetworkError(true)
        setStatus('error')
        setError('An unexpected error occurred. Please try again.')
      }
    }
  }, [joinRequestId, setClientSecret, setDeadline, setStatus, setError])

  useEffect(() => {
    clear()
    if (!useCredit) {
      fetchPaymentIntent()
    }
    return () => {
      clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinRequestId])

  const handleCreditPayment = async () => {
    if (!joinRequestId) return

    setCreditProcessing(true)
    try {
      const functions = getFunctions()
      const payWithCreditFn = httpsCallable<
        { joinRequestId: string },
        { success: boolean }
      >(functions, 'payWithCredit')

      await payWithCreditFn({ joinRequestId })
      setCreditSuccess(true)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Failed to pay with credit.')
    } finally {
      setCreditProcessing(false)
    }
  }

  if (creditSuccess) {
    return (
      <Container maxWidth='sm' sx={{ py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant='h5' color='success.main' gutterBottom>
            Payment successful
          </Typography>
          <Typography variant='body1' sx={{ mb: 3 }}>
            Your spot on the dive is confirmed. Credit has been deducted from
            your account.
          </Typography>
          <Button variant='contained' onClick={() => navigate(routes.dives)}>
            View Dives
          </Button>
        </Paper>
      </Container>
    )
  }

  if (useCredit) {
    return (
      <Container maxWidth='sm' sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant='h5' gutterBottom>
            Pay with Credit
          </Typography>

          <Alert severity='info' sx={{ mb: 2 }}>
            Your credit balance: <strong>${creditBalance.toFixed(2)}</strong>
          </Alert>

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={useCredit}
                onChange={() => {
                  setUseCredit(false)
                  setError(null)
                  fetchPaymentIntent()
                }}
              />
            }
            label='Use Credit'
            sx={{ mb: 2 }}
          />

          <Button
            variant='contained'
            fullWidth
            size='large'
            disabled={creditProcessing}
            onClick={handleCreditPayment}
          >
            {creditProcessing ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Confirm Credit Payment'
            )}
          </Button>
        </Paper>
      </Container>
    )
  }

  if (status === 'loading') {
    return (
      <Container maxWidth='sm' sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Preparing payment...</Typography>
      </Container>
    )
  }

  if (status === 'error' || (status === 'expired' && !clientSecret)) {
    return (
      <Container maxWidth='sm' sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert
            severity={status === 'expired' ? 'warning' : 'error'}
            sx={{ mb: 2 }}
          >
            {error || 'Something went wrong.'}
          </Alert>
          {networkError && (
            <Button variant='contained' onClick={fetchPaymentIntent} fullWidth>
              Retry
            </Button>
          )}
        </Paper>
      </Container>
    )
  }

  if (!clientSecret) {
    return null
  }

  if (!stripePromise) {
    return (
      <Container maxWidth='sm' sx={{ py: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity='error'>
            Payment is not configured. Please contact support.
          </Alert>
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth='sm' sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant='h5' gutterBottom>
          Complete Payment
        </Typography>

        {creditBalance > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={useCredit}
                onChange={() => {
                  setUseCredit(true)
                  setError(null)
                }}
              />
            }
            label={`Use Credit ($${creditBalance.toFixed(2)} available)`}
            sx={{ mb: 2, display: 'block' }}
          />
        )}

        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm joinRequestId={joinRequestId!} onExpired={() => {}} />
        </Elements>
      </Paper>
    </Container>
  )
}

export default Payment
